import React, { useCallback, useMemo } from 'react';
import { getColumns } from './columns';
import { Link } from 'react-router-dom';
import { Button, Popup } from 'semantic-ui-react';
import VirtualTable from 'components/VirtualTable/VirtualTable';
import {
  canPrintLabels,
  canReprintLabels,
  isB2BReprint,
  isStock,
  isWarehouseManager,
} from 'modules/user-has-permission/user-has-permission';
import ManifestsModal from 'components/ManifestsModal/ManifestsModal';
import TableWrapper from 'components/TableWrapper/TableWrapper';
import { useTerminalID } from 'hooks/useTerminalID';
import { useUser } from 'hooks/redux/useUser';

export default function PreparationOrdersTable({
  loading,
  hasNextPage,
  preparationOrders,
  selectable,
  lastResetSelectedRows,
  onRowSelect = () => {},
  onLoadMoreItems = () => {},
}) {
  const {
    user: { email: userEmail },
  } = useUser();
  const { terminalID } = useTerminalID();
  const isAllowedToAccess = useCallback(
    (row) => {
      return (
        isWarehouseManager() ||
        row.picked_at === terminalID ||
        row.status === 'READY' ||
        isB2BReprint() ||
        isStock() ||
        (row?.picked_by?.email && row?.picked_by?.email === userEmail)
      );
    },
    [userEmail, terminalID],
  );

  const accessButtonIsDisabled = useCallback(
    (row) => {
      return (
        !isWarehouseManager() &&
        !isB2BReprint() &&
        !isStock() &&
        row.picked_at !== terminalID &&
        row?.picked_by?.email &&
        row?.picked_by?.email !== userEmail
      );
    },
    [userEmail, terminalID],
  );

  const renderActions = useCallback(
    (row) => {
      return (
        <>
          {!isStock() && <ManifestsModal preparationOrderId={row.id} />}
          {isWarehouseManager ||
          ((canPrintLabels() || canReprintLabels()) &&
            row.status !== 'TRANSFER') ||
          (isStock() && row.status === 'TRANSFER') ? (
            <Popup
              className="base-tooltip"
              content={
                isAllowedToAccess(row)
                  ? 'Acceder a la orden'
                  : `No puedes acceder a esta orden porque está siendo preparada por el usuario ${row?.picked_by?.email}`
              }
              trigger={
                <Link to={`/preparation-orders/${row.reference}`}>
                  <Button
                    circular
                    icon="barcode"
                    disabled={accessButtonIsDisabled(row)}
                  />
                </Link>
              }
            />
          ) : null}
        </>
      );
    },
    [isAllowedToAccess, accessButtonIsDisabled],
  );

  const columns = useMemo(() => getColumns(renderActions), [renderActions]);

  return (
    <TableWrapper>
      <VirtualTable
        loading={loading}
        tableClassNames="orders-table"
        columns={columns}
        data={preparationOrders}
        hasNextPage={hasNextPage}
        selectable={selectable}
        lastResetSelectedRows={lastResetSelectedRows}
        onLoadMoreItems={onLoadMoreItems}
        onRowSelect={onRowSelect}
      />
    </TableWrapper>
  );
}
